import React, { useState, useEffect, useRef } from 'react';
import apiRequest from '../services/apiClient';

// Cache global para evitar peticiones duplicadas
const optionsCache = new Map();
const pendingRequests = new Map(); // Para evitar peticiones simultáneas

// Función para invalidar caché de una entidad específica
export const invalidateCache = (entityName) => {
  const keysToDelete = [];
  for (const key of optionsCache.keys()) {
    if (key.startsWith(entityName)) {
      keysToDelete.push(key);
    }
  }
  keysToDelete.forEach(key => optionsCache.delete(key));
};

// Función para limpiar todo el caché
export const clearAllCache = () => {
  optionsCache.clear();
  pendingRequests.clear();
};

const AutocompleteServerList = ({ 
  entityName, 
  selectQuery = "?$select=id,name", 
  valueProperty = "id", 
  displayProperty = "name",
  value, 
  onChange, 
  placeholder = "Select..." 
}) => {
  const [options, setOptions] = useState([]);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Cargar opciones del API con caché y evitar peticiones simultáneas
  useEffect(() => {
    const cacheKey = `${entityName}${selectQuery}`;
    
    // Si ya tenemos los datos en caché, usarlos
    if (optionsCache.has(cacheKey)) {
      const cachedOptions = optionsCache.get(cacheKey);
      setOptions(cachedOptions);
      setFilteredOptions(cachedOptions);
      return;
    }

    // Si ya hay una petición pendiente para esta entidad, esperar a que termine
    if (pendingRequests.has(cacheKey)) {
      const pendingPromise = pendingRequests.get(cacheKey);
      pendingPromise.then((items) => {
        setOptions(items);
        setFilteredOptions(items);
      }).catch((error) => {
        console.error(`Error from pending request for ${entityName}:`, error);
        setOptions([]);
        setFilteredOptions([]);
      });
      return;
    }

    const loadOptions = async () => {
      setLoading(true);
      try {
        const response = await apiRequest(`${entityName}/paged${selectQuery}&$top=100`);
        const items = response.data.items || response.data || [];
        
        // Guardar en caché
        optionsCache.set(cacheKey, items);
        
        setOptions(items);
        setFilteredOptions(items);
        
        // Resolver la promesa pendiente
        if (pendingRequests.has(cacheKey)) {
          const pendingPromise = pendingRequests.get(cacheKey);
          pendingPromise.resolve(items);
        }
      } catch (error) {
        console.error(`Error loading ${entityName}:`, error);
        setOptions([]);
        setFilteredOptions([]);
        
        // Rechazar la promesa pendiente
        if (pendingRequests.has(cacheKey)) {
          const pendingPromise = pendingRequests.get(cacheKey);
          pendingPromise.reject(error);
        }
      } finally {
        setLoading(false);
        // Limpiar la petición pendiente
        pendingRequests.delete(cacheKey);
      }
    };

    // Crear una promesa pendiente
    let resolvePromise, rejectPromise;
    const pendingPromise = new Promise((resolve, reject) => {
      resolvePromise = resolve;
      rejectPromise = reject;
    });
    pendingPromise.resolve = resolvePromise;
    pendingPromise.reject = rejectPromise;
    pendingRequests.set(cacheKey, pendingPromise);

    loadOptions();
  }, [entityName, selectQuery]);

  // Filtrar opciones basado en el término de búsqueda
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter(option =>
        option[displayProperty]?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOptions(filtered);
    }
  }, [searchTerm, options, displayProperty]);

  // Encontrar el elemento seleccionado
  useEffect(() => {
    if (value && options.length > 0) {
      const selected = options.find(option => option[valueProperty] === value);
      setSelectedItem(selected);
      if (selected) {
        setSearchTerm(selected[displayProperty] || '');
      }
    } else {
      setSelectedItem(null);
      setSearchTerm('');
    }
  }, [value, options, valueProperty, displayProperty]);

  // Manejar click fuera del componente
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        // Restaurar el término de búsqueda al valor seleccionado
        if (selectedItem) {
          setSearchTerm(selectedItem[displayProperty] || '');
        } else {
          setSearchTerm('');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectedItem, displayProperty]);

  const handleInputChange = (e) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);
    setIsOpen(true);
    
    // Si se borra el texto, limpiar la selección
    if (newSearchTerm === '') {
      onChange('');
    }
  };

  const handleOptionClick = (option) => {
    setSelectedItem(option);
    setSearchTerm(option[displayProperty] || '');
    setIsOpen(false);
    onChange(option[valueProperty]);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleClear = () => {
    setSelectedItem(null);
    setSearchTerm('');
    onChange('');
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          className="input-field pr-8"
          autoComplete="off"
        />
        
        {/* Botón de limpiar */}
        {selectedItem && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute inset-y-0 right-6 flex items-center pr-2"
          >
            <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
        
        {/* Icono de dropdown */}
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          <svg
            className={`h-4 w-4 text-gray-400 transform transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
          {loading ? (
            <div className="px-3 py-2 text-sm text-gray-500">Loading...</div>
          ) : filteredOptions.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">
              {searchTerm ? 'No results found' : 'No options available'}
            </div>
          ) : (
            filteredOptions.map((option) => (
              <button
                key={option[valueProperty]}
                onClick={() => handleOptionClick(option)}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 focus:outline-none ${
                  selectedItem && selectedItem[valueProperty] === option[valueProperty]
                    ? 'bg-blue-50 text-blue-600'
                    : 'text-gray-700'
                }`}
              >
                {option[displayProperty]}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default AutocompleteServerList;